/**
 * OE-DDX-UI-001 §1.19 / OA-04 — connectivity principles (not offline sync).
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type ConnectivityValue = {
  online: boolean;
};

const ConnectivityContext = createContext<ConnectivityValue>({ online: true });

export function ConnectivityProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    function on() {
      setOnline(true);
    }
    function off() {
      setOnline(false);
    }
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const value = useMemo(() => ({ online }), [online]);
  return (
    <ConnectivityContext.Provider value={value}>
      {children}
      {!online ? (
        <div
          role="status"
          data-testid="connectivity-banner"
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            padding: "var(--ax-space-2) var(--ax-space-4)",
            background: "var(--ax-color-warning)",
            color: "var(--ax-color-primary)",
            fontFamily: "var(--ax-type-family-sans)",
            fontSize: "var(--ax-type-sm)",
            zIndex: "var(--ax-z-toast)",
            textAlign: "center",
          }}
        >
          Sin conexión — las acciones en curso no se reintentan automáticamente.
          Vuelve a intentar cuando se restablezca la red.
        </div>
      ) : null}
    </ConnectivityContext.Provider>
  );
}

export function useConnectivity(): ConnectivityValue {
  return useContext(ConnectivityContext);
}

export function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof Error && /Failed to fetch|NetworkError|Load failed/i.test(err.message)) {
    return true;
  }
  return false;
}
